import { useGetSession } from '@repo/api/auth/useGetSession';
import { isParent } from '@repo/lib/user-lvl/isParent';
import { AccountType } from '@repo/types/enums/accountType';
import { UserLvl } from '@repo/types/enums/userLvl';

import { useCredentials } from '@/store/auth-store';

/**
 * Authorizes the current user against a route's requirements.
 *
 * Access is granted only when **both** axes match: the numeric `lvl` and the
 * `accountType`. Parents share `UserLvl.STUDENT` with students, so a `lvl`-only
 * check would let them into every student route.
 *
 * `requiredAccountTypes` defaults to regular users, which keeps parents out of
 * existing routes unless they opt in explicitly.
 */
export function useProtectedRoute(requiredLvls: UserLvl | UserLvl[], requiredAccountTypes: AccountType | AccountType[] = AccountType.User) {
  const { isLoading } = useGetSession();
  const { lvl, accountType } = useCredentials();

  const isAdmin: boolean = lvl === UserLvl.ADMIN;
  const isTeacher: boolean = lvl === UserLvl.TEACHER;
  const isStudent: boolean = lvl === UserLvl.STUDENT;

  const lvls: UserLvl[] = Array.isArray(requiredLvls) ? requiredLvls : [requiredLvls];

  const accountTypes: AccountType[] = Array.isArray(requiredAccountTypes) ? requiredAccountTypes : [requiredAccountTypes];

  const lvlAllowed: boolean = lvls.some((rank) => (rank === UserLvl.ADMIN && isAdmin) || (rank === UserLvl.TEACHER && isTeacher) || (rank === UserLvl.STUDENT && isStudent));

  // Legacy records have no accountType and are regular users.
  const currentAccountType: AccountType = isParent(accountType) ? AccountType.Parent : AccountType.User;

  const accountTypeAllowed: boolean = accountTypes.includes(currentAccountType);

  return {
    allow: lvlAllowed && accountTypeAllowed,
    isLoading,
  };
}
